import type { Timestamp } from 'firebase/firestore';
import type { Measurement, MeasurementKey } from './types';
import { MEASUREMENT_FIELDS } from './types';

/**
 * Render a measurement for display.
 * Returns '' for missing data — never '0', never 'N/A'. A zeta of 0.00 mV is a
 * real reading; a missing zeta is not, and the two must never look alike.
 */
export function formatMeasurement(m: Measurement | null, key: MeasurementKey): string {
  if (!m || m.value === null || m.value === undefined || Number.isNaN(m.value)) return '';
  const { decimals } = MEASUREMENT_FIELDS[key];
  const value = m.value.toFixed(decimals);
  if (m.sd === null || m.sd === undefined || Number.isNaN(m.sd)) return value;
  return `${value} ± ${m.sd.toFixed(decimals)}`;
}

export function formatValue(value: number | null | undefined, key: MeasurementKey): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return value.toFixed(MEASUREMENT_FIELDS[key].decimals);
}

export function toDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  return typeof ts.toDate === 'function' ? ts.toDate() : null;
}

export function formatDate(ts: Timestamp | null | undefined): string {
  const d = toDate(ts);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(ts: Timestamp | null | undefined): string {
  const d = toDate(ts);
  if (!d) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** yyyy-mm-dd for <input type="date">, in local time rather than UTC. */
export function toDateInputValue(d: Date | null): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function relativeTime(d: Date | null): string {
  if (!d) return 'never';
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

/**
 * Parse a text input into a number.
 * Empty string means "absent", which is distinct from zero — hence null, not 0.
 */
export function parseNumber(raw: string): number | null {
  const trimmed = raw.trim().replace(/\u2212/g, '-'); // unicode minus from some keyboards
  if (trimmed === '' || trimmed === '-' || trimmed === '.') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}
